import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import Icon from '../../components/Icon';
import TeacherFields, {
  EMPTY_TEACHER_PICKS,
  EMPTY_TEACHER_VALUES,
  cityGap,
  teacherPayload,
  teacherValidators,
} from '../../components/TeacherFields';
import { ErrorBanner, InfoBanner } from '../../components/Feedback';
import useForm from '../../hooks/useForm';
import * as v from '../../validation/rules';
import { REGISTRATION_CODES, UPLOAD_CODES } from '../../validation/serverCodes';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

/**
 * Teacher registration, field for field from FOCUS_Registration_Form.
 *
 * <p>The fields themselves live in {@code TeacherFields}, because Support and an admin
 * ask for exactly the same ones when they create a teacher from Registered Users. What
 * is here is what only self-registration has: the public shell, a password the
 * registrant must choose, an ID proof they must upload, and tokens on the way out.
 *
 * <p>The document lists no password for teachers. One is collected anyway — a teacher is
 * expected to sign in and watch their verification status, which needs a way in.
 */
export default function TeacherRegisterPage() {
  const { user, acceptAuth } = useAuth();
  const navigate = useNavigate();
  const [picks, setPicks] = useState(EMPTY_TEACHER_PICKS);
  const [cityError, setCityError] = useState(null);
  const [error, setError] = useState(null);

  const form = useForm({
    initialValues: EMPTY_TEACHER_VALUES,
    // Support cannot review a teacher without the document, so it is required here.
    validators: teacherValidators({ requirePassword: true, requireIdProof: true }),
    crossField: v.passwordsMatch,
  });

  if (user) return <Navigate to="/" replace />;

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    const gap = cityGap(picks);
    setCityError(gap);
    if (gap) return;
    try {
      acceptAuth(await api.registerTeacher(teacherPayload(values, picks), values.idProof));
      navigate('/', { replace: true });
    } catch (err) {
      // The city lives in the picker rather than in the form, so a rejection about it
      // is put there by hand — routing it through the form would mark it handled and
      // render it on a field that is not on the screen, which suppresses the banner and
      // shows the registrant nothing at all.
      if (err?.code === 'invalid_city') {
        setCityError(err.message);
        return;
      }
      if (!form.applyServerErrors(err, { ...REGISTRATION_CODES, ...UPLOAD_CODES })) setError(err);
    }
  });

  return (
    <AuthLayout wide>
      <form {...form.formProps} onSubmit={submit}>
        <Link to="/login" className="btn btn-ghost" style={{ marginBottom: 16, paddingLeft: 0 }}>
          <Icon name="back" size={15} />
          Back to sign in
        </Link>
        <h2>Register as a teacher</h2>
        <p className="sub">
          Your account is created immediately and enters verification. You can sign in and track its
          status; resources unlock once a Sales Representative verifies you.
        </p>
        <ErrorBanner error={error} onDismiss={() => setError(null)} />

        <div className="stack">
          <TeacherFields form={form} picks={picks} onPicks={setPicks} cityError={cityError} />

          <InfoBanner>
            Your document is stored privately and shown only to the Support and Sales staff reviewing
            your account.
          </InfoBanner>

          <button type="submit" className="btn btn-primary btn-block" disabled={form.submitting}>
            {form.submitting ? 'Submitting…' : 'Submit for verification'}
            <Icon name="arrow" size={15} />
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
